import React, {useState} from 'react'
import clsx from 'clsx'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {logIn, googleLogin} from '../../../services/AuthService'
import {toast} from 'react-toastify'
import {useNavigate} from 'react-router-dom'
import {useDispatch, useSelector} from 'react-redux'
import {getCurrentUser} from '../../../redux/slices/AuthSlices'
import {MenuComponent} from '../../../../_metronic/assets/ts/components'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email format')
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch<any>()
  const modules = useSelector((state: any) => state.auth.modules)

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data} = await logIn({email: values.email, password: values.password})
        const token = data.data.token
        const modules = JSON.stringify(data.data.modules)
        localStorage.setItem('loginToken', token)
        localStorage.setItem('modules', modules)

        // Fetch current user data and modules into Redux before navigation
        try {
          await dispatch(getCurrentUser()).unwrap()

          // Wait a moment for Redux to fully update
          await new Promise(resolve => setTimeout(resolve, 100))

          // Dispatch custom event to trigger auth state update AFTER data is loaded
          window.dispatchEvent(new Event('login'))

          toast.success('Login Successful')
          setLoading(false)

          // Navigate immediately after Redux update completes
          if (data.success) {
            navigate('/dashboard')

            // Force menu reinitiali after navigation and DOM settlement
            setTimeout(() => {
              MenuComponent.reinitialization()
            }, 1500)
          }
        } catch (userError) {
          console.error('Error fetching current user:', userError)
          toast.error('Failed to load user data. Please try again.')
          setLoading(false)
        }
      } catch (error) {
        console.error(error)
        setStatus('Invalid login credentials')
        setSubmitting(false)
        setLoading(false)
        toast.error('Login failed. Check your credentials.')
      }
    },
  })

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState)
  }

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
      </div>

      {formik.status && (
        <div className='mb-lg-7 alert alert-danger text-center'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent'
            // { 'is-invalid': formik.touched.email && formik.errors.email },
            // { 'is-valid': formik.touched.email && !formik.errors.email }
          )}
          type='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.email}</span>
          </div>
        )}
      </div>

      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6'>Password</label>
        <div className='position-relative'>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder='Password'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent'
              // { 'is-invalid': formik.touched.password && formik.errors.password },
              // { 'is-valid': formik.touched.password && !formik.errors.password }
            )}
          />
          <span
            className='position-absolute top-50 end-0 mr-10 translate-middle-y me-3 cursor-pointer'
            onClick={togglePasswordVisibility}
            style={{cursor: 'pointer', marginRight: '50px'}}
          >
            <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
          </span>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid || loading}
        >
          {loading ? (
            <span className='indicator-label'>Please wait...</span>
          ) : (
            <span className='indicator-label'>Continue</span>
          )}
        </button>
      </div>
    </form>
  )
}
